import React, { useContext, useEffect, useState } from 'react';
import Input from '../../atoms/inputs/Input';
import { injectIntl } from 'react-intl';
import { errorKeys } from '../../../constants/documentCustomization';
import { StateHelper } from '../../templates/helper/state';
import { AppContext } from '../../../contexts/AppContext';
import SelectWithFlags from '../../atoms/selects/SelectWithFlags';
import { ContactService } from '../../../services';
import Icon from '../../atoms/icons/Icon';

const VAT_VALIDATION_STATE = {
  VALID: 'valid',
  INVALID: 'invalid',
  PENDING: 'pending',
};

const InputVATValidator = (props) => {
  const { appState } = useContext(AppContext);
  const {
    canChangeCountry = true,
    defaultCountry,
    defaultVatNumber,
    isDisabled,
    shouldValidate = true,
    validationResponse,
    debounceTimer = 1000,
  } = props;
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('');
  const [requestErrors, setRequestErrors] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [vatValidationState, setVatValidationState] = useState(undefined);

  const changeCoutryCode = (e) => {
    setCountry(e);
  };

  const changeVatNumber = (e) => {
    const vatNumber = e.target.value;

    if (vatValidationState !== VAT_VALIDATION_STATE.PENDING) {
      if (!vatNumber) {
        setVatNumber('');
        setRequestErrors('');
        setVatValidationState(undefined);
      } else {
        setVatNumber(vatNumber);
      }
    }
  };

  const validateNIF = async () => {
    const iso_code = country.code;

    const response = await ContactService.vatNumberLookup(vatNumber, iso_code);

    if (response.valid === true) {
      validationResponse(country, vatNumber, response, true);
      setVatValidationState(VAT_VALIDATION_STATE.VALID);
    } else {
      validationResponse(country, vatNumber, response, false);
      setRequestErrors('invalidVATError');
      setVatValidationState(VAT_VALIDATION_STATE.INVALID);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (shouldValidate && country !== '' && vatNumber !== '') {
        setRequestErrors('');
        setVatValidationState(VAT_VALIDATION_STATE.PENDING);
        await validateNIF();
      }
    }, debounceTimer);

    return () => clearTimeout(delayDebounce);

    // eslint-disable-next-line
  }, [country, vatNumber]);

  useEffect(() => {
    const getCountries = async () => {
      const countriesRes =
        (await StateHelper.fetchCountries(
          appState.accountId,
          () => setRequestErrors(errorKeys.fetchError),
          [
            { key: 'id', value: 'id' },
            { key: 'code', value: 'country_code' },
            { key: 'tax_location_id', value: 'tax_location_id' },
          ]
        )) || [];

      if (!defaultCountry) {
        setCountry(countriesRes[0]);
      } else {
        let country = undefined;
        if (defaultCountry.country_id) {
          country = countriesRes.find(
            (country) => country.id === defaultCountry.country_id
          );
        } else {
          country = countriesRes.find(
            (country) =>
              country.tax_location_id === defaultCountry.tax_location_id
          );
        }

        setCountry(country);
      }

      setCountries(countriesRes);
    };

    getCountries();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (defaultVatNumber && vatNumber === '') {
      setVatNumber(defaultVatNumber);
    }

    // eslint-disable-next-line
  }, [defaultVatNumber]);

  return (
    <div className='input-vat-validator d-flex flex-column w-100'>
      <div className='d-flex flex-row w-100'>
        <SelectWithFlags
          id='vat-country-select'
          value={country}
          options={countries}
          onChange={changeCoutryCode}
          disabled={
            isDisabled ||
            !canChangeCountry ||
            vatValidationState === VAT_VALIDATION_STATE.PENDING
          }
        />

        <div className='vat-input-container d-flex flex-row flex-grow-1 position-relative'>
          <Input
            id='vat-input'
            className='vat-input'
            type='text'
            value={vatNumber}
            onChange={changeVatNumber}
            disabled={isDisabled}
          />
          {(vatNumber && (
            <>
              {(vatValidationState === VAT_VALIDATION_STATE.PENDING && (
                <div className='vat-validation-state'>
                  <Icon className='icon fas fa-circle-notch fa-spin color-gray-500' />
                </div>
              )) || <></>}

              {((vatValidationState === VAT_VALIDATION_STATE.VALID ||
                !shouldValidate) && (
                <div
                  data-testid='vat-validation-valid'
                  className='vat-validation-state'
                >
                  <Icon className='icon fas fa-check color-brand-200' />
                </div>
              )) || <></>}

              {(vatValidationState === VAT_VALIDATION_STATE.INVALID && (
                <div className='vat-validation-state'>
                  <Icon className='icon fas fa-times color-red-200' />
                </div>
              )) || <></>}
            </>
          )) || <></>}
        </div>
      </div>

      {(requestErrors && (
        <div
          data-testid='country-error-message'
          className='input-error'
          id='error-message'
        >
          <Icon className='icon fas fa-circle-exclamation' />
          {props.intl.messages[requestErrors]}
        </div>
      )) || <></>}
    </div>
  );
};

export default injectIntl(InputVATValidator);
